const DEFAULT_PLACES_KEY = "AIzaSyC1J7Kv7n2r4UfMTPGIGIUEZWlabNUGqCE";

function placesApiKey(): string {
  const fromEnv = import.meta.env.VITE_PLACES_API_KEY as string | undefined;
  if (typeof fromEnv === "string" && fromEnv.trim().length > 0) {
    return fromEnv.trim();
  }
  return DEFAULT_PLACES_KEY;
}

export type PlacePrediction = {
  placeId: string;
  description: string;
};

export type PlaceAddress = {
  street: string;
  postalCode: string;
  city: string;
  formattedAddress: string;
};

function normalizePlaceResourceName(placeId: string): string {
  const trimmed = placeId.trim();
  if (trimmed.startsWith("places/")) return trimmed;
  return `places/${trimmed}`;
}

export async function placesAutocomplete(
  query: string,
  languageCode = "de",
  regionCode = "DE"
): Promise<{ predictions: PlacePrediction[]; error?: string }> {
  const apiKey = placesApiKey();
  if (!apiKey || query.trim().length < 3) {
    return { predictions: [] };
  }

  const response = await fetch("https://places.googleapis.com/v1/places:autocomplete", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": apiKey,
    },
    body: JSON.stringify({
      input: query.trim(),
      languageCode,
      regionCode,
      includedPrimaryTypes: ["geocode"],
    }),
  });

  if (!response.ok) {
    return { predictions: [], error: `HTTP ${response.status}` };
  }

  const data = (await response.json()) as {
    suggestions?: Array<{
      placePrediction?: { place?: string; placeId?: string; text?: { text?: string } };
    }>;
  };
  const predictions: PlacePrediction[] = [];
  for (const suggestion of data.suggestions ?? []) {
    const prediction = suggestion.placePrediction;
    if (!prediction) continue;
    const resourceName = prediction.place?.trim() ?? "";
    const placeId = prediction.placeId?.trim() ?? "";
    const resolved = resourceName || (placeId ? normalizePlaceResourceName(placeId) : "");
    const description = prediction.text?.text?.trim() ?? "";
    if (!resolved || !description) continue;
    predictions.push({ placeId: resolved, description });
  }
  return { predictions };
}

export async function fetchPlaceDetails(
  placeId: string
): Promise<{ address?: PlaceAddress; error?: string }> {
  const apiKey = placesApiKey();
  if (!apiKey || !placeId) return {};

  const resourceName = normalizePlaceResourceName(placeId);
  const encoded = encodeURIComponent(
    resourceName.startsWith("places/") ? resourceName.slice("places/".length) : resourceName
  );
  const response = await fetch(`https://places.googleapis.com/v1/places/${encoded}`, {
    headers: {
      "X-Goog-Api-Key": apiKey,
      "X-Goog-FieldMask": "id,formattedAddress,location,addressComponents",
    },
  });

  if (!response.ok) {
    return { error: `HTTP ${response.status}` };
  }

  const result = (await response.json()) as {
    id?: string;
    formattedAddress?: string;
    addressComponents?: Array<{ types?: string[]; longText?: string }>;
  };

  let city = "";
  let postalCode = "";
  let route = "";
  let streetNumber = "";
  for (const component of result.addressComponents ?? []) {
    const types = component.types ?? [];
    const longName = component.longText ?? "";
    if (types.includes("locality")) city = longName;
    if (types.includes("postal_code")) postalCode = longName;
    if (types.includes("route")) route = longName;
    if (types.includes("street_number")) streetNumber = longName;
  }

  return {
    address: {
      street: [route, streetNumber].filter(Boolean).join(" ").trim(),
      postalCode,
      city,
      formattedAddress: result.formattedAddress ?? "",
    },
  };
}

